import { useAuthStore, hostRole, hostUrl } from '@sentinel/web-core';
import { useNavigate } from '@tanstack/react-router';
import { Button, Modal } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Appearance } from './Appearance';
import { Destinations } from './Destinations';
import { EmailPreferences } from './EmailPreferences';
import { initials } from './initials';
import { LinkedIdentities } from './LinkedIdentities';
import { SpaceChoice } from './SpaceChoice';
import { UserHeader } from './UserHeader';

/** The user card (header proposal 11): the avatar with name and organization in the topbar;
 *  a click opens who is signed in, where one works, the space, theme and language, linked
 *  accounts, e-mail notifications and signing out. */
export function UserCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.me);
  const logout = useAuthStore((s) => s.logout);
  const [open, setOpen] = useState(false);

  if (me === null) return null;
  const close = () => setOpen(false);
  const signOut = async () => {
    close();
    await logout();
    // Three hosts: the `accounts.` session ends too, or the next visit would hand back in.
    if (hostRole() === 'single') await navigate({ to: '/login' });
    else window.location.assign(`${hostUrl('accounts')}/logout`);
  };
  const organization = me.organization?.name ?? t('shell.user.noOrganization');

  return (
    <>
      <button
        type="button"
        className="user-btn"
        aria-haspopup="dialog"
        aria-label={t('shell.user.open', { name: me.displayName, organization })}
        onClick={() => setOpen(true)}
      >
        <span className="av" aria-hidden="true">
          {initials(me.displayName)}
        </span>
        <span className="tx" aria-hidden="true">
          <b>{me.displayName}</b>
          <span>{organization}</span>
        </span>
      </button>
      <Modal
        open={open}
        title={t('shell.user.card')}
        icon="user"
        onClose={close}
        closeLabel={t('common.actions.close')}
        footer={
          <div className="uc-foot">
            <Button onClick={() => void signOut()} icon="logout">
              {t('shell.user.logout')}
            </Button>
            <Button onClick={close}>{t('common.actions.close')}</Button>
          </div>
        }
      >
        <UserHeader me={me} />
        <Destinations onLeave={close} />
        {me.organization === null ? null : <SpaceChoice onChosen={close} />}
        <Appearance />
        <LinkedIdentities />
        {me.organization === null ? null : <EmailPreferences />}
      </Modal>
    </>
  );
}
